<script lang="ts">
	//Design system components
	import { Alert, Button, Container, Input, Modal, Text } from '@Shelly/shelly-design-system';
	//Stores
	import { get } from 'svelte/store';
	import { auth_locked_devices, device_auths } from 'src/stores/device-runtime/local-auth';
	import {
		discovered_statuses,
		discovered_settings,
		discovered_info,
	} from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import { authLockedDeviceModal, unauthorisedRequests } from 'src/stores/device-runtime/firmware-ui';
	//Services
	import { format } from 'svelte-i18n';
	import { localReq } from '../../../services/http';
	//Types
	// import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { is_gen_2, real_id, get_local_ip, get_mode } from '../../../helpers/interface_helpers';
	import { allDiscovered } from 'src/stores/features/discovered';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import { devices_config } from '@devices/index';
	import {
		applyXTDeviceTransform,
		buildDiscoveredDevice,
		submitDeviceForInclusion,
	} from 'src/helpers/inclusion_helpers';
	import type { LocalRequest } from 'src/types/requests/local';
	import type { UnauthorisedRequestData } from 'src/types/UnauthorisedRequestData';
	import {
		get_local_settings,
		get_local_status,
		get_local_shelly_info,
		trigger401ToGetAuthHeader,
	} from 'src/services/local_requests';
	import { computeHa1, parseDigestRealm } from 'src/helpers/device_auth_helpers';
	import { refresh_verified_local_ip } from 'src/helpers/local_ip_probe_helpers';

	const id = $authLockedDeviceModal;
	const realID = real_id(id);

	/** try if device is already in the account. if so load from interface data else load from discovered; fallback by real_id for store keyed by channel id*/
	const device =
		$devices[id] ||
		$allDiscovered[id] ||
		$devices[realID] ||
		$allDiscovered[realID] ||
		$discovered_info[id] ||
		$discovered_info[realID] ||
		Object.values($devices || {}).find((d) => d && real_id(d.id) === realID);

	// we need to skip the check for the device inclusion process
	let skip_check = true;
	const imageName = device?.type ? device.type + '.png' : 'unknown-device.svg';
	let image = `images/device_images/${imageName}`;

	const isGen2 = is_gen_2(device);

	let valueUser;
	let valuePass;

	let loading;
	let error: string;

	const onCloseAuthentication = () => {
		authLockedDeviceModal.set(undefined);
	};

	/** When forceRefresh is true, always triggers a fresh 401 to get a new nonce (required when user enters new password). */
	const ensureGen2AuthHeader = async (
		device,
		baseUrl,
		gen2Payload,
		unauthorisedRequest,
		forceRefresh = false,
	) => {
		if (!forceRefresh && unauthorisedRequest.responseHeaders)
			return unauthorisedRequest.responseHeaders;
		await localReq(device, baseUrl, 'post', gen2Payload, 10000, false, false, true);
		const authId = real_id(device.id);
		const updatedRequest = get(unauthorisedRequests)[authId] || get(unauthorisedRequests)[realID];
		if (!updatedRequest?.responseHeaders) {
			error = $format('not_retrieved_authentication_header', {
				default: 'Could not retrieve authentication header',
			});
			return null;
		}
		unauthorisedRequest.responseHeaders = updatedRequest.responseHeaders;
		return unauthorisedRequest.responseHeaders;
	};

	const resolveDeviceIp = (): string | null => {
		if (device?.ip) return device.ip;
		const url =
			$unauthorisedRequests[realID]?.url || $unauthorisedRequests[real_id(device?.id)]?.url;
		if (url) {
			try {
				return new URL(url).hostname;
			} catch {
				//
			}
		}
		const localIp = get_local_ip(device?.id);
		return typeof localIp === 'string' && localIp ? localIp : null;
	};

	const buildGen2BaseUrl = (deviceIp: string | null, requestUrl?: string): string | null => {
		if (typeof requestUrl === 'string' && requestUrl) {
			try {
				const parsedUrl = new URL(requestUrl);
				return parsedUrl.origin + '/rpc/';
			} catch {
				//
			}
		}
		if (typeof deviceIp === 'string' && deviceIp) {
			const base = deviceIp.substring(0, 4) === 'http' ? deviceIp : 'http://' + deviceIp;
			return base.endsWith('/') ? base + 'rpc/' : base + '/rpc/';
		}
		return null;
	};

	const buildGen2Payload = () => ({
		id: Date.now(),
		method: 'Shelly.GetStatus',
		src: 'ShellyApp',
	});

	const resolveUnauthorisedRequest = async (
		deviceAuthId: string,
		deviceIp: string | null,
	): Promise<UnauthorisedRequestData | null> => {
		let unauthorisedRequest =
			get(unauthorisedRequests)[realID] || get(unauthorisedRequests)[deviceAuthId];

		if (unauthorisedRequest) return unauthorisedRequest;

		if (isGen2) {
			const baseUrl = buildGen2BaseUrl(deviceIp);
			if (!baseUrl) return null;
			return {
				responseHeaders: null,
				payload: buildGen2Payload(),
				url: baseUrl,
			};
		}

		if (deviceIp) {
			await trigger401ToGetAuthHeader(device, deviceIp);
			unauthorisedRequest =
				get(unauthorisedRequests)[deviceAuthId] || get(unauthorisedRequests)[realID];
		}

		return unauthorisedRequest ?? null;
	};

	const authenticate = async () => {
		const deviceAuthId = real_id(device?.id);
		let unauthorisedRequest = $unauthorisedRequests[realID] || $unauthorisedRequests[deviceAuthId];
		let res;
		loading = true;
		error = undefined;

		if (!device) {
			error = $format('device_not_found', { default: 'Device not found' });
			loading = false;
			return;
		}
		// On a second phone that was already logged in when the device got locked, the local 401
		// challenge may never have been captured (the device can be flagged locked purely from the
		// cloud auth_en + same-SSID heuristic). For Gen2 we synthesize an empty request and rebuild
		// re-runs local discovery) used to be required.
		if (!unauthorisedRequest) {
			if (isGen2) {
				unauthorisedRequest = { responseHeaders: null, payload: {}, url: '' };
			} else {
				error = $format('missing_request_data', { default: 'Missing request data' });
				loading = false;
				return;
			}
		}
		if (!isGen2 && (typeof valueUser !== 'string' || !valueUser.trim())) {
			error = $format('enter_username', { default: 'Please enter a username' });
		}
		if (typeof valuePass !== 'string' || !valuePass.trim()) {
			error = $format('enter_password', { default: 'Please enter a password' });
			loading = false;
			return;
		}

		// Re-probe the LAN for a currently-reachable IP rather than trusting a possibly stale cached
		// endpoint/IP — the root cause behind "works only after logout/login". Falls back to the
		// existing resolution when probing yields nothing, so the normal flow is unaffected.
		const verifiedIp = isGen2 ? await refresh_verified_local_ip(device.id) : false;
		const deviceIp = typeof verifiedIp === 'string' && verifiedIp ? verifiedIp : resolveDeviceIp();

		try {
			if (isGen2) {
				const baseUrl = (() => {
					// Prefer the freshly verified, reachable IP.
					if (typeof verifiedIp === 'string' && verifiedIp) {
						const base =
							verifiedIp.substring(0, 4) === 'http' ? verifiedIp : 'http://' + verifiedIp;
						return base.endsWith('/') ? base + 'rpc/' : base + '/rpc/';
					}
					if (typeof unauthorisedRequest.url === 'string' && unauthorisedRequest.url) {
						try {
							const parsedUrl = new URL(unauthorisedRequest.url);
							return parsedUrl.origin + '/rpc/';
						} catch {
							//
						}
					}
					if (typeof deviceIp === 'string' && deviceIp) {
						const base =
							deviceIp.substring(0, 4) === 'http' ? deviceIp : 'http://' + deviceIp;
						return base.endsWith('/') ? base + 'rpc/' : base + '/rpc/';
					}
					return null;
				})();
				if (!baseUrl) {
					error = $format('missing_device_url', { default: 'Missing device URL' });
					loading = false;
					return;
				}
				const gen2Payload = buildGen2Payload();

				const responseHeaders = await ensureGen2AuthHeader(
					device,
					baseUrl,
					gen2Payload,
					unauthorisedRequest,
					true,
				);

				if (!responseHeaders) {
					loading = false;
					return;
				}
				// FE-1998: persist the non-reversible ha1, never the plaintext password.
				const realm = parseDigestRealm(responseHeaders);
				const gen2Auth = {
					auth_data: responseHeaders,
					ha1: computeHa1(realm, valuePass),
					nc: 1,
				};
				device_auths.update(deviceAuthId, gen2Auth);
				if (deviceAuthId !== realID) device_auths.update(realID, { ...gen2Auth });
				res = await localReq(device, baseUrl, 'post', gen2Payload, 10000, false, true);
			} else {
				const gen1Auth = { user: valueUser, pass: valuePass };
				device_auths.update(deviceAuthId, gen1Auth);
				if (deviceAuthId !== realID) device_auths.update(realID, { ...gen1Auth });
				res = await localReq(
					device,
					unauthorisedRequest.url,
					'post',
					unauthorisedRequest.payload,
					10000,
					true,
					false,
				);
			}
		} catch (err) {
			console.error('UnlockAuthLockedDevice authenticate error', err);
			error = $format('error_global', { default: 'Something went wrong!' });
			loading = false;
			return;
		}

		loading = false;
		if (res?.isok) {
			auth_locked_devices.remove(deviceAuthId);
			if (deviceAuthId !== realID) auth_locked_devices.remove(realID);
			error = undefined;
			onCloseAuthentication();
			if ($includeDeviceModal.show) {
				if (!device.ip && deviceIp) {
					device.ip = deviceIp;
				}

				let info =
					$discovered_info[id] || $discovered_info[device.id] || $discovered_info[realID];
				if (!info && deviceIp) {
					const infoRes: LocalRequest = await get_local_shelly_info(device, deviceIp);
					if (infoRes?.mac) {
						info = infoRes;
						info.pulled = Date.now();
						discovered_info.update((info_list) => {
							return { ...info_list, [device.id]: info };
						});
					}
				}

				if (isGen2 && info?.model) {
					device.type = info.model;
				}

				const xtResult = applyXTDeviceTransform(device, info);
				if (xtResult?.image) {
					image = xtResult.image;
				} else if (device.type) {
					image = `images/device_images/${device.type}.png`;
				}

				let mode;
				let config = devices_config[device.type];
				let status = $discovered_statuses[device.id];
				let settings = $discovered_settings[device.id];

				if (
					typeof config !== 'undefined' &&
					typeof status !== 'undefined' &&
					typeof settings !== 'undefined'
				) {
					mode = get_mode(device, status, settings);
				} else if (deviceIp) {
					status = await get_local_status(device, deviceIp, skip_check);
					if (status.isok) {
						status.pulled = Date.now();
						discovered_statuses.update((status_list) => {
							return { ...status_list, [device.id]: status };
						});

						const settings: any = await get_local_settings(device, deviceIp, skip_check);
						if (settings.isok) {
							settings.pulled = Date.now();
							discovered_settings.update((settings_list) => {
								return { ...settings_list, [device.id]: settings };
							});
							mode = get_mode(device, status, settings);
						}
					}
				}

				const _device = buildDiscoveredDevice({
					device,
					image,
					config,
					status,
					settings,
					mode,
				});

				includeDeviceModal.set({
					show: false,
					step: 0,
					selected: undefined,
				});
				submitDeviceForInclusion(_device, settings);
			}
		} else {
			error = $format('invalid_credentials', { default: 'Invalid credentials' });
			device_auths.remove(deviceAuthId);
			if (deviceAuthId !== realID) device_auths.remove(realID);
		}
	};

	const clearError = () => {
		if (typeof error !== 'undefined') error = undefined;
	};
</script>

<Modal
	on:close={onCloseAuthentication}
	showModal={typeof $authLockedDeviceModal !== 'undefined'}
	title={$format('title_authenticate_device', {
		default: 'Authentication required',
	})}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text class="mb-s"
			>{!isGen2
				? $format('auth_locked_credentials', {
						default: 'Please enter your device authentication credentials',
					})
				: $format('auth_locked_password', {
						default: 'Please enter your device authentication password',
					})}</Text
		>
		{#if !isGen2}
			<Input
				aria-labelledby="authentication-username"
				id="authentication-username"
				bind:value={valueUser}
				type="text"
				class="mb-s"
				on:enter={authenticate}
				on:input={clearError}
				label={$format('placeholder_username')}
				disabled={loading}
			/>
		{/if}

		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<Input
			a11yProps={{
				iconButtonAriaLabel: $format('authentication-password', {
					default: 'Authentication password',
				}),
			}}
			aria-labelledby="authentication-password"
			id="authentication-password"
			bind:value={valuePass}
			on:enter={authenticate}
			on:input={clearError}
			type="password"
			class="pb-s"
			label="*******"
			disabled={loading}
		/>

		{#if error}
			<Alert
				type="danger"
				heading={$format('failed_authentication_error_message_error', {
					default: 'Authentication failed',
				})}
				message={error}
			/>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap text-capitalize"
			size="l"
			responsiveSize={{
				sm: 'm',
			}}
			{loading}
			on:click={authenticate}
		>
			{$format('button_unlock')}
		</Button>
	</div>
</Modal>
